import { Trans } from '@lingui/macro'
import { Checkbox, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { Dispatch } from 'redux'
import { NumberFormatDefault } from './Common'

type PdfElementStyle = 'card' | 'border' | 'plain'

interface PdfTypeProps {
  /** Lets the element overflow onto the next page instead of being moved as a whole. */
  allowPDFWrap?: boolean
  pdfFontSize?: string
  /** Starts a new page before the element. */
  pageBreak?: boolean
  pdfElementStyle?: PdfElementStyle
  [key: string]: unknown
}

interface PdfPropsFormProps {
  typeProps: PdfTypeProps
  showFontSizeOption?: boolean
  /** Form editor store dispatch. */
  dispatch: Dispatch
  /** Dot separated path for fields or already split path for groups. */
  depth: string | string[]
  /** Marks editing an injectable element instead of the form. */
  injectable?: boolean
  isGroup?: boolean
  /** Hides the page break option for elements nested in a group. */
  isInsideGroup?: boolean
}

/**
 * Editor section with settings of how an element is rendered in the generated PDF.
 */
export const PdfPropsForm = ({
  typeProps,
  showFontSizeOption = false,
  dispatch,
  depth,
  injectable,
  isGroup,
  isInsideGroup = false
}: PdfPropsFormProps) => {
  const { allowPDFWrap, pdfFontSize, pageBreak, pdfElementStyle } = typeProps

  const handleDispatch = (fieldName: keyof PdfTypeProps, fieldValue: unknown) => {
    const toSet = isGroup ? fieldValue : { ...typeProps, [fieldName]: fieldValue }
    const field = isGroup ? fieldName : 'typeProps'
    const newDepth = typeof depth === 'string' ? depth.split('.') : depth

    dispatch({
      type: 'FIELD',
      injectable,
      depth: newDepth,
      fieldName: field,
      fieldValue: toSet
    })
  }

  return (
    <div style={{ marginTop: 10 }}>
      <Typography variant="h6">
        <Trans>Pdf props</Trans>
      </Typography>
      <RadioGroup
        row
        defaultValue={'plain'}
        value={pdfElementStyle}
        onChange={(e) => {
          const val = e.currentTarget.value
          handleDispatch('pdfElementStyle', val)
        }}
      >
        <FormControlLabel
          value="card"
          control={<Radio />}
          label={<Trans>Display as a card</Trans>}
        />
        <FormControlLabel
          value="border"
          control={<Radio />}
          label={<Trans>Display with border</Trans>}
        />
        <FormControlLabel
          value="plain"
          control={<Radio />}
          label={<Trans>Without styling</Trans>}
        />
      </RadioGroup>
      <div style={{ padding: 5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(allowPDFWrap)}
              onChange={(e) => {
                handleDispatch('allowPDFWrap', e.target.checked)
              }}
            />
          }
          label={<Trans>PDF_PROPS_ALLOW_OVERFLOWING</Trans>}
        />
      </div>
      {!isInsideGroup && (
        <div style={{ padding: 5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(pageBreak)}
                onChange={(e) => {
                  handleDispatch('pageBreak', e.target.checked)
                }}
              />
            }
            label={<Trans>PDF_PROPS_PAGE_BREAK_BEFORE</Trans>}
          />
        </div>
      )}
      {showFontSizeOption && (
        <TextField
          style={{ marginTop: 8 }}
          label={<Trans>Font size</Trans>}
          value={pdfFontSize || ''}
          fullWidth
          variant="outlined"
          multiline
          InputProps={{ inputComponent: NumberFormatDefault }}
          onChange={(e) => {
            handleDispatch('pdfFontSize', e.target.value)
          }}
        />
      )}
    </div>
  )
}
